import React, { useMemo } from 'react';
import Select from 'react-select';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useTheme } from '@material-ui/core';
import { FormLabel, FormError } from 'components/FormTexts';
import * as Styled from './Dropdown.styled';

const getCustomDropdownStyles = (error, theme) => ({
  control: (provided, { isDisabled }) => ({
    ...provided,
    color: theme.palette.common.black,
    backgroundColor: isDisabled
      ? theme.palette.xpl.action.disabled
      : provided.backgroundColor,
    borderRadius: '4px',
    border: `1px solid ${
      !error ? theme.palette.xpl.stoke : theme.palette.common.error
    }`,
    '&:hover': {
      border: `1px solid ${
        !error
          ? theme.palette.xpl.secondary.default
          : theme.palette.common.error
      }`,
    },
    '&:focus-within': {
      border: `1px solid ${
        !error
          ? theme.palette.xpl.secondary.default
          : theme.palette.common.error
      }`,
    },
    '&:focus': {
      border: `1px solid ${
        !error
          ? theme.palette.xpl.secondary.default
          : theme.palette.common.error
      }`,
    },
    boxShadow: 'none',
  }),
  option: (provided) => ({
    ...provided,
    color: theme.palette.common.darkerText,
    backgroundColor: theme.palette.common.white,
    '&:hover': {
      backgroundColor: '#F9FCFD',
      color: theme.palette.common.black,
    },
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    backgroundColor: '#EBF7FA',
    color: theme.palette.common.secondary,
    borderRadius: '6px 0px 0px 6px',
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    backgroundColor: '#EBF7FA',
    color: theme.palette.common.secondary,
    ':hover': {
      backgroundColor: theme.palette.common.secondary,
      color: theme.palette.common.white,
    },
  }),
  placeholder: (provided) => ({
    ...provided,
    fontSize: '14px',
    color: theme.palette.common.black,
  }),
  singleValue: (provided, { isDisabled }) => ({
    ...provided,
    color: isDisabled ? theme.palette.xpl.text.secondary : provided.color,
  }),
});

const Dropdown = (props) => {
  const {
    className,
    classes,
    isMulti,
    label,
    error,
    components,
    styles,
    ...passThroughProps
  } = props;

  const theme = useTheme();

  //Hide the indicator separator if not a multi select dropdown
  const customComponents = useMemo(() => {
    return {
      IndicatorSeparator: () => null,
      ...components,
    };
  }, [components]);

  //Override some of the style properties to fit the design. As mentioned here - https://react-select.com/components
  const customStyles = useMemo(() => {
    return {
      ...getCustomDropdownStyles(error, theme),
      ...styles,
    };
  }, [styles, error, theme]);

  return (
    <Styled.Wrapper className={classnames(classes.root, className)}>
      {label && <FormLabel className={classes.label}>{label}</FormLabel>}
      <Select
        isMulti={isMulti}
        className={classes.select}
        styles={customStyles}
        components={customComponents}
        theme={(reactSelectTheme) => ({
          ...reactSelectTheme,
          colors: {
            ...reactSelectTheme.colors,
            primary25: '#F9FCFD',
          },
        })}
        {...passThroughProps}
      />
      {error && <FormError>{error}</FormError>}
    </Styled.Wrapper>
  );
};

Dropdown.propTypes = {
  //All the other props are passed on to the select component. Check here - https://react-select.com/props#select-props
  className: PropTypes.string,
  classes: PropTypes.shape({
    root: PropTypes.string,
    select: PropTypes.string,
    label: PropTypes.string,
  }),
  label: PropTypes.string,
  error: PropTypes.string,
  isMulti: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ).isRequired,
  defaultValue: PropTypes.object,
  isDisabled: PropTypes.bool, // Not named disabled like the rest to keep it consistent with the react-select props.
  // Override the components in react select.
  components: PropTypes.object,
  styles: PropTypes.object,
  placeholder: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  onChange: PropTypes.func,
};

Dropdown.defaultProps = {
  isMulti: false,
  label: null,
  error: null,
  isDisabled: false,
  className: '',
  classes: {
    root: '',
    select: '',
    label: '',
  },
  components: {},
  styles: {},
  placeholder: '-Select-',
  onChange: () => {},
};

export default Dropdown;
